import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { Circle, CircleCheck, CircleDot } from "lucide-react";
import { cn } from "~/lib/utils";

// ==================
// StepperContext
// ==================

type StepperContextValue<T = unknown> = {
  stepperState: T;
  setStepperState: Dispatch<SetStateAction<T>>;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const StepperContext = createContext<StepperContextValue | null>(null);

/**
 * Access the stepper context with an optional generic for typed state.
 *
 * Usage: `useStepperContext<MyStepperState>()` to get typed `stepperState`.
 */
function useStepperContext<T = unknown>(): StepperContextValue<T> {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error("useStepperContext must be used within a <Stepper> provider");
  }
  return context as StepperContextValue<T>;
}

// ==================
// StepperIndicator
// ==================

type StepperIndicatorItem =
  | {
      type: "border";
      borderType: "solid" | "dashed";
    }
  | {
      type: "label";
      label: string;
      stepIndex: number;
    };

interface StepperIndicatorProps {
  stepLabels: string[];
  className?: string;
}

const StepperIndicator = ({ stepLabels, className }: StepperIndicatorProps) => {
  const { currentStep } = useStepperContext();

  const labelAndSeparatorArray = stepLabels.reduce<StepperIndicatorItem[]>(
    (acc, current, index) => {
      if (index > 0) {
        const isPreviousStepCompleted = index <= currentStep;
        const separator: StepperIndicatorItem = {
          type: "border",
          borderType: isPreviousStepCompleted ? "solid" : "dashed",
        };
        const labelItem: StepperIndicatorItem = {
          type: "label",
          label: current,
          stepIndex: index,
        };

        return [...acc, separator, labelItem];
      }
      return [
        {
          type: "label",
          label: current,
          stepIndex: index,
        },
      ];
    },
    []
  );

  return (
    <div className={cn("flex gap-2 items-center w-full justify-between", className)}>
      {labelAndSeparatorArray.map((item, index) => {
        if (item.type === "border") {
          return (
            <div
              key={`separator-${index}`}
              className={cn("border-b border-muted-foreground grow", {
                "border-dashed": item.borderType === "dashed",
              })}
            />
          );
        }

        const StepIcon =
          item.stepIndex < currentStep
            ? CircleCheck
            : item.stepIndex === currentStep
              ? CircleDot
              : Circle;

        return (
          <div key={`stepper-label-${index}`} className="flex items-center gap-1.5 text-sm">
            <StepIcon
              className={cn("size-6", {
                "fill-muted-foreground [&_circle]:stroke-muted-foreground [&_path]:stroke-background":
                  item.stepIndex < currentStep,
                "stroke-foreground": item.stepIndex === currentStep,
                "stroke-muted-foreground": item.stepIndex > currentStep,
              })}
            />
            <span
              className={cn(
                "text-muted-foreground",
                item.stepIndex === currentStep && "text-foreground"
              )}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ==================
// StepperItems
// ==================

interface StepperItemsProps {
  children: React.ReactNode[];
}

const StepperItems = ({ children }: StepperItemsProps) => {
  const { currentStep } = useStepperContext();

  if (!Array.isArray(children)) {
    throw new Error("StepperItems expects children to be an array of React nodes.");
  }

  const currentChild = children.at(currentStep);

  if (!currentChild) {
    throw new Error(`No child found at currentStep index: ${currentStep}`);
  }

  return <>{currentChild}</>;
};

// ==================
// Stepper
// ==================

interface StepperProps {
  initialValue: unknown;
  initialStep?: number;
  children: React.ReactNode;
}

const Stepper = ({ initialValue, initialStep, children }: StepperProps) => {
  const [stepperState, setStepperState] = useState(initialValue);
  const [currentStep, setCurrentStep] = useState(initialStep ?? 0);

  const stepperContextValue: StepperContextValue = useMemo(
    () => ({ stepperState, setStepperState, currentStep, setCurrentStep }),
    [stepperState, currentStep]
  );

  return <StepperContext.Provider value={stepperContextValue}>{children}</StepperContext.Provider>;
};

export {
  StepperContext,
  useStepperContext,
  type StepperContextValue,
  StepperIndicator,
  type StepperIndicatorProps,
  StepperItems,
  type StepperItemsProps,
  Stepper,
  type StepperProps,
};
